import type { Lang } from '../i18n';
import type { Lighting } from '../optics/eye';

/** Internal render size (ARCHITECTURE §3). Both divisible by 64 so every pyramid level is exact. */
export const RENDER_W = 1152;
export const RENDER_H = 704;

export type SceneId = 'chart' | 'home' | 'city_day' | 'city_night' | 'night_lights';
export const SCENE_IDS: SceneId[] = ['chart', 'home', 'city_day', 'city_night', 'night_lights'];

export interface SceneLayer {
  id: string;
  /** Object vergence of this depth layer, D. */
  V: number;
  draw(ctx: CanvasRenderingContext2D, lang: Lang): void;
  /** Optional emissive content (neon signs, screens) added on top of `draw` with HDR intensity `emissiveScale`. */
  emissive?: (ctx: CanvasRenderingContext2D, lang: Lang) => void;
  emissiveScale?: number;
}

/** HDR emitter in degrees of visual angle (SCENES). Intensity is relative to diffuse white = 1. */
export interface SceneLight {
  layer: string;
  x: number;
  y: number;
  rx: number;
  ry: number;
  color: [number, number, number];
  intensity: number;
}

export type TagKey = 'tag.far' | 'tag.mid' | 'tag.near' | 'tag.m4' | 'tag.m30' | 'tag.m60' | 'tag.cm75' | 'tag.cm45';

/** Distance tag drawn as an HTML overlay (never blurred), anchored in degrees. */
export interface SceneTag {
  x: number;
  y: number;
  key: TagKey;
}

/**
 * Night tone mapping (D-016): linear display up to `knee`, logarithmic compression of luminance from the knee
 * up to `white`, so that halos, glare and streaks around HDR lights stay visible on a screen.
 */
export interface ToneCurve {
  knee: number;
  k: number;
  white: number;
}

export const NIGHT_TONE: ToneCurve = { knee: 0.3, k: 0.3, white: 500 };

export interface Scene {
  id: SceneId;
  hfov: number;
  lighting: Lighting;
  exposure: number;
  /** Far layer first. */
  layers: SceneLayer[];
  lights(lang: Lang): SceneLight[];
  tags: SceneTag[];
  /** Absent: the default day curve (linear to 0.8, soft knee to 1). */
  tone?: ToneCurve;
}
